<template>
  <div class="video">
    <video
      ref="video"
      class="video__video lazyload"
      loading="lazy"
      :loop="loop ? true : false"
      :muted="isMuted"
      :autoplay="autoplay ? true : false"
      playsinline="1"
      preload="auto"
      :poster="poster"
      @loadedmetadata="onLoadmetadata"
      @pause="onPause"
      @play="onPlay"
      @timeupdate="onTimeupdate"
      @click.prevent="togglePlay"
      @mouseover="playVideo"
    >
      <source
        v-if="src"
        type="video/mp4"
        :src="src"
      >
      <template v-else-if="srcset">
        <source
          v-if="getHdSource(srcset)"
          :src="getHdSource(srcset)"
          type="video/mp4"
        >
        <source
          v-if="getHlsSource(srcset)"
          :src="getHlsSource(srcset)"
          type="application/x-mpegURL"
        >
        <source
          v-if="getSdSource(srcset)"
          :src="getSdSource(srcset)"
          type="video/mp4"
        >
      </template>
    </video>

    <div
      v-if="mobileStyling && showControls"
      class="video__mobile-controls"
    >
      <button
        v-show="!isPlaying"
        class="icon-button--compact"
        type="button"
        @click.prevent="handlePlay"
      >
        <span class="icon-button__label">{{ $string('video.play') }}</span>
        <icon-play-circle />
      </button>
    </div>
    <div
      v-else-if="showControls"
      class="video__controls"
    >
      <button
        v-show="!isPlaying"
        class="icon-button play"
        type="button"
        :aria-label="$string('video.play')"
        @click.prevent="handlePlay"
      >
        <icon-play />
      </button>
    </div>

    <div
      v-if="!mobileStyling && controls  && showControls"
      ref="toolbar"
      class="video__toolbar"
      :class="{ 'has-controls' : controls }"
    >
      <div class="video__toolbar-player">
        <button
          v-show="!isPlaying"
          class="icon-button play"
          type="button"
          :aria-label="$string('video.play')"
          @click.prevent="handlePlay"
        >
          <icon-play />
        </button>

        <button
          v-show="isPlaying"
          class="icon-button pause"
          type="button"
          :aria-label="$string('video.pause')"
          @click.prevent="handlePause"
        >
          <icon-pause />
        </button>
      </div>

      <div class="video__toolbar-progress">
        <progress
          ref="progress"
          class="video__progress"
          min="0"
          value="0"
          @click.prevent="handleClickProgress"
        >
          <span
            ref="progressBar"
            class="video__progress-bar"
          />
        </progress>
      </div>

      <div class="video__toolbar-times body-2">
        <span
          class="video__times-spent"
          v-text="spentTime"
        />
        <span class="video__times-divider">/</span>
        <span
          class="video__times-spent"
          v-text="totalTime"
        />
      </div>

      <button
        class="icon-button fullscreen"
        type="button"
        :aria-label="$string('video.fullscreen')"
        @click.prevent="handleFullscreen"
      >
        <icon-fullscreen />
      </button>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Video element
 * -----------------------------------------------------------------------------
 * Global interactive video element.
 * - Styles are imported in critical styles.
 * - Must be imported as <video-element> as <video> is a default element.
 *
 * @param {String} poster - Poster image.
 * @param {String} src - Video link. Will override srcset.
 * @param {String} srcset - Collection of video links.
 * @param {Boolean} loop - Loop video, or not.
 * @param {Boolean} muted - Mute video, or not.
 * @param {Boolean} autoplay - Autoplay video, or not.
 * @param {Boolean} controls - Show default video controls, or not.
 * @param {Boolean} shopTheLook - If video is shown in modal, or not.
 *
 */
import { mapState } from 'vuex'

import IconFullscreen from '~icons/misc/fullscreen.svg'
import IconPause from '~icons/misc/pause.svg'
import IconPlayCircle from '~icons/misc/play-circle.svg'
import IconPlay from '~icons/misc/play.svg'

export default {
  name: 'VideoElement',

  components: {
    IconPlay,
    IconPlayCircle,
    IconPause,
    IconFullscreen,
  },

  props: {
    poster: {
      type: String,
      default: '',
    },
    src: {
      type: String,
      default: '',
    },
    srcset: {
      type: Object,
      default: () => ({}),
    },
    loop: {
      type: Boolean,
      default: true,
    },
    muted: {
      type: Boolean,
      default: true,
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
    controls: {
      type: Boolean,
      default: false,
    },
    shopTheLook: {
      type: Boolean,
      default: false,
    },
    showControls: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isPausing: false,
      isPlaying: true,
      spentTime: '00:00',
      totalTime: '00:00',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute if screen is mobile or video is in Shop the Look modal.
     * @returns {Boolean}
     */
    mobileStyling() {
      if (this.isMobile || this.shopTheLook) {
        return true
      }

      return false
    },

    /**
     * Compute muted state.
     * @returns {Boolean}
     */
    isMuted() {
      if (this.autoplay || this.muted) {
        return true
      }

      return false
    },
  },

  mounted() {
    document.addEventListener('fullscreenchange', this.handleFullScreenChange)

    if (this.autoplay) {
      this.handlePlay()
    } else {
      this.isPlaying = false
    }
  },

  methods: {

    /**
     * Toggle play/pause video.
     */
    togglePlay() {
      if (this.isPlaying) {
        this.handlePause()
        return
      }

      this.handlePlay()
    },

    /**
     * Handle play video.
     */
    handlePlay() {
      this.$refs.video.play()

      if (!this.$refs.toolbar) {
        return
      }

      this.$refs.toolbar.classList.remove('is-paused')
      this.$refs.toolbar.classList.add('is-playing')
    },

    /**
     * Handle pause video.
     */
    handlePause() {
      this.$refs.video.pause()

      if (!this.$refs.toolbar) {
        return
      }

      this.$refs.toolbar.classList.remove('is-playing')
      this.$refs.toolbar.classList.add('is-paused')
    },

    /**
     * Handle pause video.
     */
    handleFullscreen() {
      this.$refs.video.requestFullscreen()
    },

    /**
     * Handle fullscreen change.
     */
    handleFullScreenChange() {
      this.$refs.video.classList.toggle('fullscreen')
    },

    /**
     * Handle click progress bar.
     * @param {Event} event - Click event.
     */
    handleClickProgress(event) {
      const pos = event.offsetX / this.$refs.progress.offsetWidth
      this.$refs.video.currentTime = pos * this.$refs.video.duration
    },

    /**
     * On video play event.
     */
    onPlay() {
      this.isPausing = false
      this.isPlaying = true
    },

    /**
     * On video pause event.
     */
    onPause() {
      this.isPausing = true
      this.isPlaying = false
    },

    /**
     * On time update event.
     */
    onTimeupdate() {
      if (!this.$refs.progress) {
        return
      }

      if (!this.$refs.progress.getAttribute('max')) {
        this.$refs.progress.setAttribute('max', this.$refs.video.duration)
        this.totalTime = this.timeParse(this.$refs.video.duration)
      }

      this.$refs.progress.value = this.$refs.video.currentTime
      this.$refs.progressBar.style.width = `${Math.floor((this.$refs.video.currentTime / this.$refs.video.duration) * 100)}%`

      this.spentTime = this.timeParse(this.$refs.video.currentTime)
    },

    /**
     * On load metadata event.
     */
    onLoadmetadata() {
      if (!this.$refs.progress) {
        return
      }

      this.$refs.progress.setAttribute('max', this.$refs.video.duration)
      this.totalTime = this.timeParse(this.$refs.video.duration)
    },

    /**
     * Convert number value into two digits before 10
     * @param {Number} - day / month
     * @returns {String} - converted value
     */
    convertTwoDigit(value) {
      return `00${Math.floor(value)}`.slice(-2)
    },

    /**
     * Format epoch time into human-readable date
     * @param {Number} sec - epoch time
     * @returns {String} - Formated time string
     */
    timeParse(sec) {
      let min = 0
      let seconds = sec
      min = Math.floor(seconds / 60)
      seconds -= min * 60
      return `${this.convertTwoDigit(min)}:${this.convertTwoDigit(seconds)}`
    },

    /**
     * Get M3U8 Video source
     * @param {Object} sources - List of video sources returned from GraphQL.
     * @returns {String}
     */
    getHlsSource(sources) {
      const hls = sources.find((source) => source.format === 'm3u8')
      return hls ? hls.url : false
    },

    /**
     * Get SD Video source
     * @param {Object} sources - List of video sources returned from GraphQL.
     * @returns {String}
     */
    getSdSource(sources) {
      const sd = sources.find((source) => source.url.includes('.SD'))
      return sd ? sd.url : false
    },

    /**
     * Get HD Video source
     * @param {Object} sources - List of video sources returned from GraphQL.
     * @returns {String}
     */
    getHdSource(sources) {
      const hd = sources.find((source) => source.url.includes('.HD'))
      return hd ? hd.url : false
    },

    /**
     * On mouseover play video.
     */
    playVideo() {
      if (!this.isPlaying) {
        this.handlePlay()
      }
    },
  },
}
</script>
